import { Dialog } from '@base-ui/react/dialog'
import React, { useRef } from 'react'
import styled, { css } from 'styled-components'
import { Button, type ButtonProps } from 'sutra/components/Button'
import { Close } from 'sutra/components/Icons'
import { Tooltip } from 'sutra/components/Tooltip/Tooltip'
import { accents, semantic } from 'sutra/tokens/colors'
import { motion } from 'sutra/tokens/motion'
import { radii } from 'sutra/tokens/radii'
import { shadows } from 'sutra/tokens/shadows'
import { typography } from 'sutra/tokens/typography'

// ── Types ─────────────────────────────────────────────────────────────────────

export type PopoverModalSize = 'sm' | 'md' | 'lg' | 'xl'

export interface HeaderButton {
  icon: React.ReactNode
  // Rendered as both the aria-label and the hover tooltip.
  label: string
  onClick: () => void
}

// The subset of Sutra `Button` props a caller may tune on the built-in footer's
// OK button. `danger` swaps the default `primary` variant for `destructive`.
export type PopoverModalOkButtonProps = Pick<
  ButtonProps,
  'danger' | 'loading' | 'disabled' | 'variant'
>

export interface PopoverModalCancelButtonProps {
  disabled?: boolean
}

export interface PopoverModalProps {
  isOpen: boolean
  onClose: () => void
  content: React.ReactNode
  $size?: PopoverModalSize
  // Non-transient alias. styled-components strips `$`-prefixed props when
  // this component is wrapped via styled(PopoverModal) — this plain prop
  // survives that wrapping and takes precedence when both are given.
  size?: PopoverModalSize
  showClose?: boolean
  // Rendered in the fixed header, to the left of the button cluster. Plain
  // ReactNode (not a `content`-style slot) — callers own their own text
  // styling and truncation is handled by the header's own layout.
  title?: React.ReactNode
  // Extra icon buttons rendered as siblings of the close button, in the
  // same header cluster — Close always renders last (rightmost).
  additionalHeaderButtons?: HeaderButton[]
  // Rendered in a fixed (non-scrolling) footer, right-aligned — pass buttons
  // directly, PopoverModal owns the row layout. Omitted entirely (not
  // rendered empty) when not given, so Content stays Popup's last child.
  footer?: React.ReactNode
  // Built-in OK / Cancel footer. Rendered only when `onOk` is given and no
  // explicit `footer` is — an explicit `footer` always wins. Cancel calls
  // `onClose`; OK calls `onOk` and does not close by itself.
  onOk?: () => void | Promise<void>
  okText?: React.ReactNode
  cancelText?: React.ReactNode
  // Puts the OK button in its loading state (it also stops responding).
  confirmLoading?: boolean
  okButtonProps?: PopoverModalOkButtonProps
  cancelButtonProps?: PopoverModalCancelButtonProps
  // Overrides the size preset. A bare number is pixels. The popup stays
  // `width: 100%` capped at this value, so it still shrinks on a small viewport.
  width?: number | string
  // Overrides the 24px padding around the body (not the header or footer).
  bodyPadding?: number | string
  // Keeps a press on the scrim from closing. Escape and the close button
  // still do.
  disablePointerDismissal?: boolean
  // Keeps Escape from closing. The scrim press and the close button still do.
  disableEscapeDismissal?: boolean
  'data-testid'?: string
  // Overlay z-index for callers embedded in their own stacking context (e.g.
  // a modal opened from inside a popover that already sits at a high
  // z-index) — mirrors sutra Select's `zIndex` prop.
  zIndex?: number
  className?: string
}

// ── Size styles ───────────────────────────────────────────────────────────────

const maxWidths: Record<PopoverModalSize, number> = {
  sm: 400,
  md: 560,
  lg: 720,
  xl: 960,
}

// ── Styled ────────────────────────────────────────────────────────────────────

// Enter/exit are driven by Base UI's `data-starting-style` / `data-ending-style`
// attributes, which it keeps on the element until the exit transition has
// finished — that is what holds the popup mounted long enough to animate out.
const Backdrop = styled(Dialog.Backdrop)<{ $zIndex: number }>`
  position: fixed;
  inset: 0;
  z-index: ${({ $zIndex }) => $zIndex};
  background: rgba(10, 13, 18, 0.5);
  opacity: 1;
  transition: opacity ${motion.duration.base}ms ${motion.ease};

  &[data-starting-style],
  &[data-ending-style] {
    opacity: 0;
  }
`

// Fills the screen above Backdrop and centers the popup. Base UI treats a
// press on it (rather than on the popup inside) as a scrim click.
const Viewport = styled(Dialog.Viewport)<{ $zIndex: number }>`
  position: fixed;
  inset: 0;
  z-index: ${({ $zIndex }) => $zIndex};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`

const Popup = styled(Dialog.Popup)<{ $maxWidth: string }>`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: ${({ $maxWidth }) => $maxWidth};
  max-height: 85vh;
  background: ${semantic.bg};
  border-radius: ${radii.md}px;
  box-shadow: ${shadows.xl};
  overflow: hidden;
  outline: none;
  opacity: 1;
  transform: translateY(0) scale(1);
  transition:
    opacity ${motion.duration.base}ms ${motion.ease},
    transform ${motion.duration.base}ms ${motion.ease};

  &[data-starting-style],
  &[data-ending-style] {
    opacity: 0;
    transform: translateY(8px) scale(0.98);
  }
`

// The scrolling box itself carries no padding, so its scrollbar renders
// flush against Popup's real edge instead of sitting inset — the 24px
// "global padding" lives one level in, on ContentInner, so it never affects
// where the scrollbar draws.
const Content = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
`

const ContentInner = styled.div<{ $padding: string }>`
  padding: ${({ $padding }) => $padding};
`

// A fixed (non-scrolling) header, flex-shrink:0 so Content is the only part
// of Popup that scrolls. Title and buttons are its two flex children — one
// coordinate system for both, instead of a sticky title bar the caller has
// to hand-tune to clear a separately-anchored floating button row.
const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-shrink: 0;
  padding: 16px 24px;
  background: ${semantic.bg};
  border-bottom: 1px solid ${semantic.border};
`

// min-width: 0 is what lets a long title actually ellipsize inside a flex
// row instead of pushing HeaderButtonRow past the popup edge. The semibold
// weight is set here so every caller's plain-text or <span> title reads as a
// title (matching Drawer's title); a caller that passes its own weighted
// element still wins.
const HeaderTitle = styled.div`
  flex: 1;
  min-width: 0;
  font-weight: ${typography.weights.semibold};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

// Holds Close plus any caller-supplied additionalHeaderButtons as one row.
const HeaderButtonRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`

// The bottom-edge mirror of Header: a fixed (non-scrolling) flex child of
// Popup, rendered after Content rather than as a sticky child inside it — a
// sticky footer only locks in place once its own static position has
// scrolled up to the sticky offset, so it visibly slides with the content
// until then. Only rendered when `footer` is given (see PopoverModalProps).
const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  flex-shrink: 0;
  padding: 16px 24px;
  background: ${semantic.bg};
  border-top: 1px solid ${semantic.border};
`

const toCssLength = (value: number | string): string =>
  typeof value === 'number' ? `${value}px` : value

const headerButtonStyles = css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  color: ${semantic.fgSubtle};
  background: transparent;
  border: none;
  border-radius: ${radii.xs}px;
  cursor: pointer;
  transition: color ${motion.duration.base}ms ${motion.ease};

  &:hover {
    color: ${semantic.fg};
  }
  &:focus-visible {
    outline: 2px solid ${accents.reflectionBlue[500]};
    outline-offset: 2px;
  }
`

const CloseButton = styled(Dialog.Close)`
  ${headerButtonStyles}
`

const HeaderIconButton = styled.button`
  ${headerButtonStyles}
  font-size: 16px;
`

// ── Component ─────────────────────────────────────────────────────────────────

export const PopoverModal = ({
  isOpen,
  onClose,
  content,
  $size = 'md',
  size,
  showClose = true,
  title,
  additionalHeaderButtons = [],
  footer,
  onOk,
  okText = 'OK',
  cancelText = 'Cancel',
  confirmLoading = false,
  okButtonProps,
  cancelButtonProps,
  width,
  bodyPadding = 24,
  disablePointerDismissal = false,
  disableEscapeDismissal = false,
  'data-testid': testId,
  zIndex = 1000,
  className,
}: PopoverModalProps): React.ReactElement => {
  const resolvedSize = size ?? $size

  const okDanger = okButtonProps?.danger ?? false
  const resolvedFooter =
    footer ??
    (onOk ? (
      <>
        <Button
          variant="secondary"
          disabled={cancelButtonProps?.disabled}
          onClick={onClose}
        >
          {cancelText}
        </Button>
        <Button
          variant={
            okButtonProps?.variant ?? (okDanger ? 'destructive' : 'primary')
          }
          danger={okDanger}
          disabled={okButtonProps?.disabled}
          loading={confirmLoading || okButtonProps?.loading}
          onClick={() => {
            void onOk()
          }}
        >
          {okText}
        </Button>
      </>
    ) : undefined)

  // Freezes the rendered props for the duration of the exit animation. The
  // popup stays mounted until its exit transition finishes, so a caller that
  // changes `content`/`size`/`showClose` in the same render that clears
  // `isOpen` (e.g. resetting a "which size is open" selector back to a
  // default) would otherwise make the still-animating popup visibly snap to
  // the new value mid-close. Gated on the `isOpen` prop itself — it carries
  // no render lag.
  const latestRef = useRef({
    content,
    resolvedSize,
    showClose,
    title,
    additionalHeaderButtons,
    footer: resolvedFooter,
    width,
    bodyPadding,
  })
  if (isOpen) {
    latestRef.current = {
      content,
      resolvedSize,
      showClose,
      title,
      additionalHeaderButtons,
      footer: resolvedFooter,
      width,
      bodyPadding,
    }
  }
  const renderContent = latestRef.current.content
  const renderSize = latestRef.current.resolvedSize
  const renderShowClose = latestRef.current.showClose
  const renderTitle = latestRef.current.title
  const renderHeaderButtons = latestRef.current.additionalHeaderButtons
  const renderFooter = latestRef.current.footer
  const renderWidth = latestRef.current.width
  const renderBodyPadding = latestRef.current.bodyPadding

  const popupRef = useRef<HTMLDivElement>(null)

  return (
    <Dialog.Root
      open={isOpen}
      disablePointerDismissal={disablePointerDismissal}
      onOpenChange={(open, details) => {
        if (open) return
        if (disableEscapeDismissal && details.reason === 'escape-key') return
        onClose()
      }}
    >
      <Dialog.Portal>
        <Backdrop $zIndex={zIndex} />
        <Viewport className={className} $zIndex={zIndex}>
          <Popup
            ref={popupRef}
            initialFocus={popupRef}
            data-testid={testId}
            $maxWidth={toCssLength(renderWidth ?? maxWidths[renderSize])}
          >
            <Header>
              <HeaderTitle>{renderTitle}</HeaderTitle>
              <HeaderButtonRow>
                {renderHeaderButtons.map((button, index) => (
                  <Tooltip key={index} content={button.label}>
                    <HeaderIconButton
                      type="button"
                      aria-label={button.label}
                      onClick={button.onClick}
                    >
                      {button.icon}
                    </HeaderIconButton>
                  </Tooltip>
                ))}
                {renderShowClose && (
                  <CloseButton aria-label="Close">
                    <Close />
                  </CloseButton>
                )}
              </HeaderButtonRow>
            </Header>
            <Content>
              <ContentInner $padding={toCssLength(renderBodyPadding)}>
                {renderContent}
              </ContentInner>
            </Content>
            {renderFooter && <Footer>{renderFooter}</Footer>}
          </Popup>
        </Viewport>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

PopoverModal.displayName = 'PopoverModal'
